import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import {
  ClientRequestDetails,
  ClientRequestDetailsApiResponse,
  ClientRequestDetailsResult,
} from '../models/api-request-response-info.model';

@Injectable({
  providedIn: 'root',
})
export class ApiRequestResponseInfoService {

  private readonly endpoint = 'APIRequestSearch/clientRequestDetails';

  constructor(
    private http: HttpClient,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private clientSelectionService: ClientSelectionService
  ) {}

  getClientRequestDetails(
    skip = 0,
    count = 10,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: {
      claimKey?: string;
      apiMethod?: string;
      status?: string;
      requestDate?: string | Date | null;
      requestedBy?: string;
    }
  ): Observable<ClientRequestDetailsResult> {

    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('count', count.toString())
      .set('sortBy', sortBy ?? '')
      .set('sortDir', sortDir ?? '');

    if (filters?.claimKey?.trim()) {
      params = params.set('claimKey', filters.claimKey.trim());
    }

    if (filters?.apiMethod?.trim()) {
      params = params.set('apiMethod', filters.apiMethod.trim());
    }

    if (filters?.status?.trim()) {
      params = params.set('status', filters.status.trim());
    }

    const requestDateValue = this.toApiDate(filters?.requestDate);
    if (requestDateValue) {
      params = params.set('requestDate', requestDateValue);
    }

    if (filters?.requestedBy?.trim()) {
      params = params.set('requestedBy', filters.requestedBy.trim());
    }

    return this.http
      .get<{ clientRequests: ClientRequestDetailsApiResponse[]; totalCount: number }>(
        this.getUrl(),
        { headers: this.getHeaders(), params }
      )
      .pipe(
        map(response => ({
          clientRequests: (response.clientRequests ?? []).map(r => this.mapRecord(r)),
          totalCount: response.totalCount ?? 0,
        }))
      );
  }

  private getUrl(): string {
    return `${environment.apiUrl}${this.endpoint}`;
  }

  private getHeaders(): HttpHeaders {
    const token = this.oktaAuth.getAccessToken();
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();

    if (!token) {
      throw new Error('No access token found');
    }

    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${token}`,
      ClientDBName: clientDbName,
    });
  }

  private mapRecord(r: ClientRequestDetailsApiResponse): ClientRequestDetails {
    return {
      requestId: r.RequestId,
      claimKey: r.ClaimKey,
      apiMethod: r.APIMethod,
      requestDate: r.RequestDate,
      requestedBy: r.RequestedBy,
      responseDate: r.ResponseDate,
      RequestFile: r.RequestFile,
      ResponseFile: r.ResponseFile,
      responseTime: r.ResponseTime,
      status: r.Status,
    };
  }

  private toApiDate(value?: string | Date | null): string {
    if (!value) return '';

    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return value.trim();
  }
}
